import { useEffect } from 'react';
import type { EditorView } from '@codemirror/view';
import {
  editorTopLine,
  scrollEditorToLine,
  previewTopLine,
  wysiwygTopBlockIndex,
  previewTopBlockIndex,
  scrollWysiwygToBlockIndex,
  scrollPreviewToBlockIndex,
} from '../scroll/scrollMap';
import type { RenderedPreviewHandle } from '../components/RenderedPreview';

export type EditorKind = 'plaintext' | 'wysiwyg';

export interface ScrollSyncOptions {
  /** The rendered preview's scroll container. */
  previewEl: HTMLElement | null;
  /**
   * Imperative handle into the virtualized preview. Editor→preview scrolls
   * route through `previewHandle.scrollToLine` so unmounted target blocks
   * can be reached (the virtualizer mounts the target before scrolling).
   * The hook still uses `previewEl` for the reverse direction
   * (preview→editor) — that one reads from the topmost visible block,
   * which IS mounted.
   */
  previewHandle: RenderedPreviewHandle | null;
  /** The active editor's scroll container (CodeMirror `.cm-scroller` / Crepe root). */
  editorEl: HTMLElement | null;
  /** Which editor is active — picks the mapping strategy. */
  editorKind: EditorKind;
  /** Live CodeMirror view (plaintext only); needed for source-line mapping. */
  editorView: EditorView | null;
  /** Sync only while split view is active. */
  enabled: boolean;
}

/**
 * Content-aware split-view scroll sync. Unlike a naive scroll *ratio* (which
 * drifts because the two panes have different content heights), this aligns the
 * *visible content* on both sides using the markdown source line as the shared
 * coordinate:
 *   - plaintext↔preview: `editorTopLine` → `scrollPreviewToLine`, and
 *     `previewTopLine` → `scrollEditorToLine` (preview blocks carry `data-line`,
 *     CodeMirror knows the source line of its viewport top).
 *   - wysiwyg↔preview: ProseMirror has no source positions, so map by block
 *     index — the i-th top-level editor block aligns to the i-th preview block.
 *
 * Echo suppression uses a short-lived "leader" guard: whichever side the user
 * scrolls becomes the leader and drives the other; the follower's *echoed*
 * scroll events (fired async by the programmatic scroll) are ignored until the
 * leader has been idle ~120ms. Left scrolls → right catches up, and vice-versa,
 * with no oscillation/drift.
 */
export function useScrollSync({
  previewEl,
  previewHandle,
  editorEl,
  editorKind,
  editorView,
  enabled,
}: ScrollSyncOptions): void {
  useEffect(() => {
    if (!enabled || !previewEl || !editorEl) return;

    type Side = 'editor' | 'preview';
    let leader: Side | null = null;
    let releaseTimer: ReturnType<typeof setTimeout> | undefined;

    const lead = (side: Side): boolean => {
      // While one side leads, ignore the other's echoed scroll.
      if (leader && leader !== side) return false;
      leader = side;
      if (releaseTimer) clearTimeout(releaseTimer);
      releaseTimer = setTimeout(() => {
        leader = null;
      }, 120);
      return true;
    };

    const onEditorScroll = (): void => {
      if (!lead('editor')) return;
      if (editorKind === 'plaintext') {
        // Route through the handle so the virtualizer can scroll to a block
        // that may not currently be mounted.
        previewHandle?.scrollToLine(editorTopLine(editorView));
      } else {
        scrollPreviewToBlockIndex(previewEl, wysiwygTopBlockIndex(editorEl));
      }
    };

    const onPreviewScroll = (): void => {
      if (!lead('preview')) return;
      if (editorKind === 'plaintext') {
        scrollEditorToLine(editorView, previewTopLine(previewEl));
      } else {
        scrollWysiwygToBlockIndex(editorEl, previewTopBlockIndex(previewEl));
      }
    };

    editorEl.addEventListener('scroll', onEditorScroll, { passive: true });
    previewEl.addEventListener('scroll', onPreviewScroll, { passive: true });
    return () => {
      if (releaseTimer) clearTimeout(releaseTimer);
      editorEl.removeEventListener('scroll', onEditorScroll);
      previewEl.removeEventListener('scroll', onPreviewScroll);
    };
  }, [previewEl, previewHandle, editorEl, editorKind, editorView, enabled]);
}
